/**
 * Tokenising the formula language.
 *
 * The reference writes formulas in Ruby and hands them to `eval`. We cannot do
 * that — a formula travels inside a project file that may have come from
 * anywhere, and the technical spec (9, risk T5) forbids dynamic evaluation —
 * but that is an argument against `eval`, not against the language. So the
 * language is reimplemented: this file turns Ruby source into tokens, the
 * parser turns tokens into a tree, and the interpreter walks the tree over a
 * closed value model. No host object is ever reachable from a formula.
 *
 * What is scanned here is the expression grammar. Anything that would define
 * or reach outside — `def`, `class`, `module`, backticks, globals — is rejected
 * at the token or the parse, the same events the reference's Ripper subclass
 * rejects.
 */

export class FormulaSyntaxError extends Error {
  constructor(message: string, readonly offset: number) {
    super(message);
    this.name = "FormulaSyntaxError";
  }
}

/** Raised for the constructs the reference blacklists outright. */
export class ForbiddenFormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ForbiddenFormulaError";
  }
}

export type TokenType =
  | "int"
  | "float"
  | "string"
  | "symbol"
  | "regexp"
  | "ident"
  | "const"
  | "keyword"
  | "op"
  | "newline"
  | "eof";

/** A piece of a double-quoted string: literal text or `#{...}` source. */
export type StringPart =
  | { kind: "text"; text: string }
  | { kind: "code"; source: string; offset: number };

export interface Token {
  type: TokenType;
  value: string;
  parts?: StringPart[];
  flags?: string;
  offset: number;
  /** Whether whitespace preceded the token — Ruby needs it to read `f -1`. */
  spaced: boolean;
}

/**
 * Keywords the reference refuses outright (`BLACK_LIST_KW`), plus the ones that
 * only appear in definitions. `self` is on the reference's list because it
 * would expose the evaluation binding; here there is no binding to expose, but
 * refusing it keeps a formula that works there working here.
 */
const FORBIDDEN_KEYWORDS = new Set(["alias", "self", "def", "class", "module", "undef", "__method__", "binding"]);

const KEYWORDS = new Set([
  "if", "elsif", "else", "end", "unless", "while", "until", "for", "in", "do",
  "then", "begin", "rescue", "ensure", "case", "when", "return", "break",
  "next", "nil", "true", "false", "and", "or", "not", "defined?", "yield",
  "super", "retry", "redo", "raise", "lambda", "proc",
  ...FORBIDDEN_KEYWORDS,
]);

/**
 * Longest first: the scanner takes the first match, so `<=>` must be offered
 * before `<=`, and `**` before `*`.
 */
const OPERATORS = [
  "<=>", "===", "**=", "<<=", ">>=", "&&=", "||=", "...",
  "**", "==", "!=", "<=", ">=", "&&", "||", "<<", ">>", "=~", "!~",
  "+=", "-=", "*=", "/=", "%=", "|=", "&=", "^=", "..", "::", "&.",
  "+", "-", "*", "/", "%", "<", ">", "!", "=", "&", "|", "^", "~",
  "(", ")", "[", "]", "{", "}", ",", ".", "?", ":", ";",
];

/** Operators that are also method names, so `:+` is a valid symbol. */
const OPERATOR_SYMBOLS = ["<=>", "===", "**", "==", "!=", "<=", ">=", "<<", ">>",
  "[]=", "[]", "+", "-", "*", "/", "%", "<", ">", "!", "&", "|", "^", "~"];

/** Tokens after which a `/` is division rather than the start of a regexp. */
const VALUE_ENDING = new Set<TokenType>(["int", "float", "string", "regexp", "ident", "const"]);

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  let spaced = false;

  const previous = (): Token | undefined => tokens[tokens.length - 1];

  const endsValue = (): boolean => {
    const token = previous();
    if (!token) return false;
    if (token.type === "keyword") return token.value === "end" || token.value === "nil"
      || token.value === "true" || token.value === "false";
    if (token.type === "op") return token.value === ")" || token.value === "]" || token.value === "}";
    return VALUE_ENDING.has(token.type);
  };

  const push = (type: TokenType, value: string, offset: number, extra: Partial<Token> = {}): void => {
    tokens.push({ type, value, offset, spaced, ...extra });
    spaced = false;
  };

  while (index < source.length) {
    const char = source[index]!;

    // Line continuation: a trailing backslash glues the next line on.
    if (char === "\\" && (source[index + 1] === "\n" || source[index + 1] === "\r")) {
      index += source[index + 1] === "\r" && source[index + 2] === "\n" ? 3 : 2;
      spaced = true;
      continue;
    }
    if (char === " " || char === "\t" || char === "\r") {
      index += 1;
      spaced = true;
      continue;
    }
    if (char === "\n") {
      push("newline", "\n", index);
      index += 1;
      continue;
    }
    if (char === "#") {
      while (index < source.length && source[index] !== "\n") index += 1;
      continue;
    }

    const start = index;

    // -- numbers ------------------------------------------------------------
    if (isDigit(char)) {
      const scanned = scanNumber(source, index);
      index = scanned.next;
      push(scanned.float ? "float" : "int", scanned.text, start);
      continue;
    }

    // -- strings ------------------------------------------------------------
    if (char === '"' || char === "'") {
      const scanned = scanString(source, index, char);
      index = scanned.next;
      push("string", scanned.raw, start, { parts: scanned.parts });
      continue;
    }
    if (char === "%" && (source[index + 1] === "w" || source[index + 1] === "i")) {
      const scanned = scanWordList(source, index);
      if (scanned) {
        index = scanned.next;
        // A word list becomes a normal array literal for the parser: the
        // difference is only in how it was written.
        push("op", "[", start);
        scanned.words.forEach((word, position) => {
          if (position > 0) push("op", ",", start);
          if (scanned.symbols) push("symbol", word, start);
          else push("string", word, start, { parts: [{ kind: "text", text: word }] });
        });
        push("op", "]", start);
        continue;
      }
    }

    // -- symbols ------------------------------------------------------------
    if (char === ":" && source[index + 1] !== ":") {
      // `:+` and friends: an operator is a method name, so it is a symbol too.
      // `inject(:+)` is the usual way a formula sums a list.
      const operatorSymbol = OPERATOR_SYMBOLS.find((candidate) => source.startsWith(candidate, index + 1));
      if (operatorSymbol) {
        index += 1 + operatorSymbol.length;
        push("symbol", operatorSymbol, start);
        continue;
      }
      if (isSymbolStart(source[index + 1])) {
        const scanned = scanSymbol(source, index);
        index = scanned.next;
        push("symbol", scanned.name, start);
        continue;
      }
    }

    // -- regular expressions -------------------------------------------------
    if (char === "/" && !endsValue()) {
      const scanned = scanRegexp(source, index);
      index = scanned.next;
      push("regexp", scanned.source, start, { flags: scanned.flags });
      continue;
    }

    // -- identifiers, constants, keywords ------------------------------------
    if (isIdentStart(char)) {
      let end = index;
      while (end < source.length && isIdentPart(source[end]!)) end += 1;
      // Ruby allows `?` and `!` to end a method name: `empty?`, `any?`.
      if (source[end] === "?" || source[end] === "!") {
        // `a ? b : c` — a bare `?` after an identifier is the ternary when a
        // space precedes it or the next character continues an expression.
        const looksLikeMethod = source[end] === "!" ? source[end + 1] !== "=" : true;
        if (looksLikeMethod && !(source[end] === "?" && source[end - 1] === " ")) end += 1;
      }
      const word = source.slice(index, end);
      index = end;
      if (FORBIDDEN_KEYWORDS.has(word)) {
        throw new ForbiddenFormulaError(`Palavra reservada não permitida numa fórmula: ${word}`);
      }
      if (KEYWORDS.has(word)) push("keyword", word, start);
      else if (/^[A-Z]/.test(word)) push("const", word, start);
      else push("ident", word, start);
      continue;
    }

    // -- things that are never allowed ---------------------------------------
    if (char === "`") throw new ForbiddenFormulaError("Crase não é permitida numa fórmula.");
    if (char === "$") throw new ForbiddenFormulaError("Variável global não é permitida numa fórmula.");
    if (char === "@") throw new ForbiddenFormulaError("Variável de instância não é permitida numa fórmula.");

    // -- operators ------------------------------------------------------------
    const operator = OPERATORS.find((candidate) => source.startsWith(candidate, index));
    if (!operator) {
      throw new FormulaSyntaxError(`Caractere inesperado: ${JSON.stringify(char)}`, index);
    }
    index += operator.length;
    push("op", operator, start);
  }

  tokens.push({ type: "eof", value: "", offset: index, spaced });
  return tokens;
}

/* -------------------------------------------------------------------------- */
/* Scanners                                                                    */
/* -------------------------------------------------------------------------- */

function isDigit(char: string | undefined): boolean {
  return char !== undefined && char >= "0" && char <= "9";
}

function isIdentStart(char: string | undefined): boolean {
  return char !== undefined && (/[A-Za-z_]/.test(char) || char.charCodeAt(0) > 127);
}

function isIdentPart(char: string | undefined): boolean {
  return char !== undefined && (/[A-Za-z0-9_]/.test(char) || char.charCodeAt(0) > 127);
}

function isSymbolStart(char: string | undefined): boolean {
  return isIdentStart(char) || char === '"';
}

function scanNumber(source: string, start: number): { text: string; next: number; float: boolean } {
  let index = start;
  if (source[index] === "0" && /[xXbBoO]/.test(source[index + 1] ?? "")) {
    const base = source[index + 1]!.toLowerCase();
    index += 2;
    while (index < source.length && /[0-9a-fA-F_]/.test(source[index]!)) index += 1;
    const digits = source.slice(start + 2, index).replace(/_/g, "");
    const radix = base === "x" ? 16 : base === "b" ? 2 : 8;
    return { text: String(Number.parseInt(digits, radix)), next: index, float: false };
  }
  while (index < source.length && /[0-9_]/.test(source[index]!)) index += 1;
  let float = false;
  if (source[index] === "." && isDigit(source[index + 1])) {
    float = true;
    index += 1;
    while (index < source.length && /[0-9_]/.test(source[index]!)) index += 1;
  }
  if (/[eE]/.test(source[index] ?? "") && (isDigit(source[index + 1]) || (/[+-]/.test(source[index + 1] ?? "") && isDigit(source[index + 2])))) {
    float = true;
    index += 2;
    while (index < source.length && isDigit(source[index])) index += 1;
  }
  return { text: source.slice(start, index).replace(/_/g, ""), next: index, float };
}

function scanString(
  source: string,
  start: number,
  quote: string,
): { raw: string; parts: StringPart[]; next: number } {
  const parts: StringPart[] = [];
  let text = "";
  let index = start + 1;
  while (index < source.length) {
    const char = source[index]!;
    if (char === quote) {
      index += 1;
      if (text || !parts.length) parts.push({ kind: "text", text });
      return { raw: source.slice(start, index), parts, next: index };
    }
    if (char === "\\") {
      const escaped = source[index + 1];
      if (escaped === undefined) break;
      if (quote === "'") {
        // A single-quoted string only understands \\ and \'.
        text += escaped === "\\" || escaped === "'" ? escaped : `\\${escaped}`;
        index += 2;
        continue;
      }
      if (escaped === "u") {
        const braced = source[index + 2] === "{";
        const from = index + (braced ? 3 : 2);
        const to = braced ? source.indexOf("}", from) : from + 4;
        if (to > from) {
          text += String.fromCodePoint(Number.parseInt(source.slice(from, to), 16) || 0);
          index = to + (braced ? 1 : 0);
          continue;
        }
      }
      text += UNESCAPE[escaped] ?? escaped;
      index += 2;
      continue;
    }
    if (quote === '"' && char === "#" && source[index + 1] === "{") {
      if (text) {
        parts.push({ kind: "text", text });
        text = "";
      }
      const from = index + 2;
      let depth = 1;
      let cursor = from;
      while (cursor < source.length && depth > 0) {
        const inner = source[cursor]!;
        // Braces inside a nested string must not close the interpolation.
        if (inner === '"' || inner === "'") {
          const nested = scanString(source, cursor, inner);
          cursor = nested.next;
          continue;
        }
        if (inner === "{") depth += 1;
        else if (inner === "}") depth -= 1;
        cursor += 1;
      }
      if (depth > 0) throw new FormulaSyntaxError("Interpolação sem fechamento", index);
      parts.push({ kind: "code", source: source.slice(from, cursor - 1), offset: from });
      index = cursor;
      continue;
    }
    text += char;
    index += 1;
  }
  throw new FormulaSyntaxError("Texto sem aspas de fechamento", start);
}

const UNESCAPE: Record<string, string> = {
  n: "\n", t: "\t", r: "\r", "0": "\0", s: " ", e: "",
  a: "", b: "\b", f: "\f", v: "\v", "\\": "\\", '"': '"', "'": "'", "#": "#",
};

function scanSymbol(source: string, start: number): { name: string; next: number } {
  let index = start + 1;
  if (source[index] === '"') {
    const scanned = scanString(source, index, '"');
    const text = scanned.parts.map((part) => (part.kind === "text" ? part.text : "")).join("");
    return { name: text, next: scanned.next };
  }
  while (index < source.length && isIdentPart(source[index]!)) index += 1;
  if (source[index] === "?" || source[index] === "!") index += 1;
  return { name: source.slice(start + 1, index), next: index };
}

const WORD_LIST_CLOSERS: Record<string, string> = { "[": "]", "(": ")", "{": "}", "<": ">" };

function scanWordList(
  source: string,
  start: number,
): { words: string[]; symbols: boolean; next: number } | null {
  const symbols = source[start + 1] === "i";
  const opener = source[start + 2];
  if (!opener) return null;
  const closer = WORD_LIST_CLOSERS[opener] ?? opener;
  const end = source.indexOf(closer, start + 3);
  if (end < 0) return null;
  const words = source.slice(start + 3, end).split(/\s+/).filter(Boolean);
  return { words, symbols, next: end + 1 };
}

function scanRegexp(source: string, start: number): { source: string; flags: string; next: number } {
  let index = start + 1;
  let pattern = "";
  while (index < source.length) {
    const char = source[index]!;
    if (char === "\\") {
      pattern += char + (source[index + 1] ?? "");
      index += 2;
      continue;
    }
    if (char === "/") {
      index += 1;
      let flags = "";
      while (index < source.length && /[imxo]/.test(source[index]!)) {
        if (source[index] !== "o" && source[index] !== "x") flags += source[index];
        index += 1;
      }
      return { source: pattern, flags, next: index };
    }
    if (char === "\n") break;
    pattern += char;
    index += 1;
  }
  throw new FormulaSyntaxError("Expressão regular sem fechamento", start);
}
